import { TrashIcon } from '@dynamika-shared/ui/icons';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { FC, ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { inputProps, selectProps } from '../formProps';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import {
  TaskDefinitionActionArgument,
  TaskStatuses,
  CollectionElementTypeResult,
} from '@dynamika-shared/types/pmTaskDefinitions';
import {
  collectionTypeTagOptions,
  propertyOperatorOptions,
  tagConditionScopeOptions,
  taskSet,
  taskStatusesOptions,
  taskTagType,
} from '../TaskCondinions/conditionOptions';
import { LabelCheckboxActionList } from './LabelCheckboxActionList';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { getLabelsForCollectionType } from '../TaskCondinions/labelsUtils';
import { set } from 'lodash';
import { filterOptions } from './actionOptions';
import { sortOptionsByLabel } from 'src/utils/sortOptions';

type TaskArgumentActionProps = {
  children?: ReactNode;
  className?: string;
  actionIndex: number;
  argumentIndex: number;
};

export const TaskArgumentAction: FC<TaskArgumentActionProps> = ({
  actionIndex,
  argumentIndex,
}) => {
  const { setValue, watch } = useFormContext();
  const [tags] = useClinicalTrialInventorySlice(state => [state.tags ?? []]);

  const { actions } = watch('actions');

  const currentArgument = actions[actionIndex].arguments[argumentIndex];

  const labels = useClinicalTrialSlice(state => state.data?.labelGroups || []);

  const optionTags = useMemo(
    () => tags.map(({ id, name }) => ({ value: id, label: name })).sort(sortOptionsByLabel),
    [tags]
  );
  const items = useMemo(() => {
    return getLabelsForCollectionType(labels, currentArgument.collectionType);
  }, [labels, currentArgument.collectionType]);

  const setFocus = (scope: string) => {
    setValue(`actions.actions[${actionIndex}].arguments[${argumentIndex}]`, {
      ...currentArgument,
      scope,
    });
  };

  const setSource = (collectionType: string) => {
    setValue(`actions.actions[${actionIndex}].arguments[${argumentIndex}]`, {
      ...currentArgument,
      collectionType,
      collectionElementType: {},
    });
  };

  const setCollectionElementType = (type: string) => {
    const data =
      type === '.TagIdStatuses'
        ? { type, statuses: [], id: currentArgument.collectionElementType.id }
        : type === '.TagIdResultProperty'
        ? {
            type,
            id: currentArgument.collectionElementType.id,
            propertyOperator: '=',
          }
        : {
            type,
            id: currentArgument.collectionElementType.id,
          };
    setValue(
      `actions.actions[${actionIndex}].arguments[${argumentIndex}].collectionElementType`,
      data
    );
  };

  const setLabelId = (labelId: string) => {
    setValue(
      `actions.actions[${actionIndex}].arguments[${argumentIndex}].collectionElementType.id`,
      labelId
    );
  };

  const updateArgument = (
    fieldPath: string,
    value: string | TaskStatuses[]
  ) => {
    const updatedArgument = { ...currentArgument };
    set(updatedArgument, fieldPath, value);
    setValue(
      `actions.actions[${actionIndex}].arguments[${argumentIndex}]`,
      updatedArgument
    );
  };

  const removeArgument = () => {
    const currentArguments = actions[actionIndex]?.arguments || [];

    const updatedArguments = currentArguments.filter(
      (_: TaskDefinitionActionArgument, idx: number) => idx !== argumentIndex
    );
    setValue(`actions.actions[${actionIndex}].arguments`, updatedArguments);
  };

  const onSelect = (status: TaskStatuses) => {
    updateArgument('collectionElementType.statuses', [
      ...(currentArgument.collectionElementType.statuses as TaskStatuses[]),
      status,
    ]);
  };

  const onDeselect = (status: TaskStatuses) => {
    const updatedStatuses =
      currentArgument.collectionElementType.statuses.filter((s: TaskStatuses) => s !== status);
    updateArgument('collectionElementType.statuses', updatedStatuses);
  };

  return (
    <div>
      <div className='rounded-sm flex justify-between'>
        <div className={'flex gap-4 flex-grow'}>
          <div className='flex flex-wrap gap-3'>
            <Select
              {...selectProps}
              value={currentArgument.scope}
              options={tagConditionScopeOptions}
              onChange={setFocus}
              label={'Focus'}
              placeholder={'Select focus'}
              listHeight={280}
            />
            <Select
              {...selectProps}
              value={currentArgument.collectionType}
              options={collectionTypeTagOptions}
              onChange={setSource}
              label={'Source'}
              placeholder={'Select source'}
            />
            {!taskSet.has(currentArgument.collectionType) && (
              <Select
                {...selectProps}
                value={currentArgument.collectionElementType?.type}
                options={filterOptions}
                onChange={setCollectionElementType}
                label={'Filter'}
                placeholder={'Select filter'}
              />
            )}
            {taskSet.has(currentArgument.collectionType) && (
              <>
                <Select
                  {...selectProps}
                  options={optionTags}
                  value={currentArgument.collectionElementType.id}
                  onChange={id =>
                    updateArgument('collectionElementType.id', id)
                  }
                  label='Tag'
                  placeholder='Select tag'
                  autoDropdownWidth
                />
                <Select
                  {...selectProps}
                  value={currentArgument.collectionElementType?.type}
                  options={taskTagType}
                  onChange={setCollectionElementType}
                  label={'Filter'}
                  placeholder={'Select filter'}
                />
                {currentArgument.collectionElementType.type ===
                '.TagIdStatuses' ? (
                  <Select
                    {...selectProps}
                    mode='multiple'
                    maxTagCount={1}
                    options={taskStatusesOptions}
                    value={currentArgument.collectionElementType.statuses}
                    onSelect={onSelect}
                    onDeselect={onDeselect}
                    label='Statuses'
                    placeholder='Select statuses'
                  />
                ) : (
                  <Select
                    {...selectProps}
                    options={taskStatusesOptions}
                    value={currentArgument.collectionElementType.status}
                    onChange={status =>
                      updateArgument('collectionElementType.status', status)
                    }
                    label='Status'
                    placeholder='Select status'
                  />
                )}
                {currentArgument.collectionElementType.type ===
                  '.TagIdResultProperty' && (
                  <div className='flex'>
                    <Input
                      {...inputProps}
                      className='-ml-[1.15rem] mr-1.5 w-[260px]'
                      label='Result Path'
                      placeholder='Path'
                      value={currentArgument.collectionElementType.propertyPath}
                      onChange={e =>
                        updateArgument(
                          'collectionElementType.propertyPath',
                          e.target.value
                        )
                      }
                    />
                    <Select
                      {...selectProps}
                      className='mr-1.5 w-[80px]'
                      options={propertyOperatorOptions}
                      value={
                        (
                          currentArgument.collectionElementType as CollectionElementTypeResult
                        ).propertyOperator || '='
                      }
                      onChange={value =>
                        updateArgument(
                          'collectionElementType.propertyOperator',
                          value as string
                        )
                      }
                      label='Operator'
                      placeholder='='
                    />
                    <Input
                      {...inputProps}
                      label='Result Value'
                      placeholder='Value'
                      value={currentArgument.collectionElementType.propertyValue}
                      onChange={e =>
                        updateArgument(
                          'collectionElementType.propertyValue',
                          e.target.value
                        )
                      }
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <TrashIcon
          className='cursor-pointer hover:text-redOrange mt-3 min-w-3'
          width={24}
          height={24}
          onClick={removeArgument}
        />
      </div>
      {!taskSet.has(currentArgument.collectionType) && (
        <>
          {currentArgument.collectionElementType?.type === '.IDs' && (
            <LabelCheckboxActionList
              items={items}
              actionIndex={actionIndex}
              argumentIndex={argumentIndex}
            />
          )}
          {currentArgument.collectionElementType?.type === '.ID' && (
            <Select
              required={true}
              value={currentArgument.collectionElementType?.id}
              options={optionTags}
              onChange={setLabelId}
              placeholder={'Select Label'}
              className={'ml-2 max-w-[20.6rem]'}
              autoDropdownWidth
            />
          )}
        </>
      )}
    </div>
  );
};
